import React, { useState, useEffect, useContext } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';

import { SectionWrapper, Select } from 'components';
import { PMS_SERVICES } from 'Services';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import styles from './PmsConfiguration.module.scss';

const PmsConfiguration = () => {
  const { dispatch: globalDispatch } = useContext(Context);

  const [vendorSettings, setVendorSettings] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [fieldValues, setFieldValues] = useState({});
  const [createLoading, setCreateLoading] = useState(false);
  const [visibleSecrets, setVisibleSecrets] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    fetchVendorSettings();
  }, []);

  const fetchVendorSettings = async () => {
    setVendorsLoading(true);
    try {
      const response = await PMS_SERVICES.getVendorSettings();
      if (response?.type === 'success') {
        setVendorSettings(response?.data || []);
      }
    } catch (e) {
      console.error('Failed to fetch vendor settings', e);
    }
    setVendorsLoading(false);
  };

  const dispatchNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });

  const selectedVendorConfig = vendorSettings.find(
    (v) => v.vendor === selectedVendor,
  );

  const handleVendorChange = (e) => {
    const vendor = e.target.value;
    setSelectedVendor(vendor);
    setFieldValues({});
    setVisibleSecrets({});
    setFieldErrors({});
  };

  const handleFieldChange = (fieldName, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    if (fieldName === 'firm_name') {
      setFieldErrors((prev) => ({
        ...prev,
        firm_name: value.includes(',') ? 'Firm name cannot contain a comma' : '',
      }));
    }
  };

  const toggleSecretVisibility = (fieldName) => {
    setVisibleSecrets((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const isFormValid = () => {
    if (!selectedVendorConfig?.fields?.length) return false;
    const hasErrors = Object.values(fieldErrors).some(Boolean);
    if (hasErrors) return false;
    return selectedVendorConfig.fields.every((field) => {
      const value = fieldValues[field.name];
      return value !== undefined && value !== null && value !== '';
    });
  };

  const handleCreateFirm = async () => {
    if (!isFormValid()) {
      dispatchNotification('error', 'Please fill in all required fields');
      return;
    }

    setCreateLoading(true);
    try {
      const { firm_name, ...credentialFields } = fieldValues;
      const payload = {
        vendor: selectedVendor,
        firm_name,
        credentials: credentialFields,
      };

      const response = await PMS_SERVICES.createFirm(payload);

      if (response?.type === 'success') {
        dispatchNotification('success', 'Firm created successfully');
        setFieldValues({});
        setSelectedVendor('');
        setVisibleSecrets({});
        setFieldErrors({});
      } else {
        throw response?.message || response?.error;
      }
    } catch (error) {
      dispatchNotification('error', error || 'Error creating firm');
    }
    setCreateLoading(false);
  };

  return (
    <div id="pms-firm-configuration">
    <SectionWrapper title="PMS Firm Configuration">
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Configure, create, and edit firm SSM parameters for PMS vendors
          </Typography>
        </Grid>

        <Grid item xs={12} md={4} lg={3}>
          <FormControl fullWidth variant="outlined" disabled={vendorsLoading}>
            <InputLabel>Select Vendor</InputLabel>
            <Select
              label="Select Vendor"
              value={selectedVendor}
              onChange={handleVendorChange}
            >
              <MenuItem value="">
                {vendorsLoading ? 'Loading vendors...' : 'Select Vendor'}
              </MenuItem>
              {[...vendorSettings]
                .sort((a, b) =>
                  String(a.display_name).localeCompare(
                    String(b.display_name),
                    undefined,
                    { sensitivity: 'base' },
                  ),
                )
                .map((v) => (
                  <MenuItem key={v.vendor} value={v.vendor}>
                    {v.display_name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {selectedVendorConfig && !selectedVendorConfig?.ssm_required && (
          <Grid item xs={12}>
            <Typography variant="body2">
              No firm creation is needed for this vendor. Directly give your firm name and proceed.
            </Typography>
          </Grid>
        )}

        {selectedVendorConfig?.ssm_required && (
          <>
            {selectedVendorConfig.fields.map((field) => (
              <Grid item xs={12} md={4} lg={3} key={field.name}>
                {field.options ? (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={fieldValues[field.name] || ''}
                      onChange={(e) =>
                        handleFieldChange(field.name, e.target.value)
                      }
                    >
                      <MenuItem value="">Select {field.label}</MenuItem>
                      {field.options.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={field.label}
                    value={fieldValues[field.name] || ''}
                    onChange={(e) =>
                      handleFieldChange(field.name, e.target.value)
                    }
                    error={Boolean(fieldErrors[field.name])}
                    helperText={fieldErrors[field.name] || ''}
                    type={
                      field.secret && !visibleSecrets[field.name]
                        ? 'password'
                        : 'text'
                    }
                    autoComplete={field.secret ? 'new-password' : 'off'}
                    inputProps={
                      field.secret
                        ? { autoComplete: 'new-password' }
                        : { autoComplete: 'off' }
                    }
                    InputProps={
                      field.secret
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    toggleSecretVisibility(field.name)
                                  }
                                >
                                  {visibleSecrets[field.name] ? (
                                    <VisibilityOff fontSize="small" />
                                  ) : (
                                    <Visibility fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                )}
              </Grid>
            ))}

            <Grid item xs={12} className={styles.createButtonContainer}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCreateFirm}
                disabled={createLoading || !isFormValid()}
              >
                {createLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'Create Firm'
                )}
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </SectionWrapper>
    </div>
  );
};

export default PmsConfiguration;
